import { useAtom } from 'jotai';
import { atom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { toolClientService, type AccessTier } from '@/services';
import { invalidateQueries } from '@/lib/query-client';

const toolClientsPaginationAtom = atom<{ page: number; limit: number }>({
  page: 1,
  limit: 20,
});

const toolClientsSearchAtom = atom<string>('');

const toolClientsQueryAtom = atomWithQuery((get) => {
  const { page, limit } = get(toolClientsPaginationAtom);
  const search = get(toolClientsSearchAtom);
  return {
    queryKey: ['tool-clients', page, limit, search],
    queryFn: () => toolClientService.list({ page, limit, search: search || undefined }),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  };
});

const createToolClientMutationAtom = atomWithMutation(() => ({
  mutationKey: ['tool-clients', 'create'],
  mutationFn: (data: { clientId: string; accessTier?: AccessTier }) =>
    toolClientService.create(data),
  onSuccess: () => invalidateQueries(['tool-clients']),
}));

const updateTierMutationAtom = atomWithMutation(() => ({
  mutationKey: ['tool-clients', 'update-tier'],
  mutationFn: ({ clientId, accessTier }: { clientId: string; accessTier: AccessTier }) =>
    toolClientService.updateTier(clientId, accessTier),
  onSuccess: () => invalidateQueries(['tool-clients']),
}));

const rotateKeyMutationAtom = atomWithMutation(() => ({
  mutationKey: ['tool-clients', 'rotate-key'],
  mutationFn: (clientId: string) => toolClientService.rotateKey(clientId),
  onSuccess: () => invalidateQueries(['tool-clients']),
}));

const deleteToolClientMutationAtom = atomWithMutation(() => ({
  mutationKey: ['tool-clients', 'delete'],
  mutationFn: (clientId: string) => toolClientService.delete(clientId),
  onSuccess: () => invalidateQueries(['tool-clients']),
}));

export function useToolClients() {
  const [query] = useAtom(toolClientsQueryAtom);
  return query;
}

export function useToolClientsPagination() {
  return useAtom(toolClientsPaginationAtom);
}

export function useToolClientsSearch() {
  return useAtom(toolClientsSearchAtom);
}

export function useCreateToolClient() {
  const [mutation] = useAtom(createToolClientMutationAtom);
  return mutation;
}

export function useUpdateTier() {
  const [mutation] = useAtom(updateTierMutationAtom);
  return mutation;
}

export function useRotateKey() {
  const [mutation] = useAtom(rotateKeyMutationAtom);
  return mutation;
}

export function useDeleteToolClient() {
  const [mutation] = useAtom(deleteToolClientMutationAtom);
  return mutation;
}
